/**
 * Shared primitives for CI timeline components (MachineTimeline + NodeTimeline).
 *
 * Keeps pass/fail/skip/in_progress color mapping and duration formatting in one
 * place so the two timelines can't drift.
 */

export const TIMELINE_STATUS_COLORS: Record<string, string> = {
  pass: "bg-emerald-500",
  fail: "bg-rose-500",
  skip: "bg-muted-foreground/40",
  in_progress: "bg-amber-500",
};

export function timelineBarColor(status: string): string {
  return TIMELINE_STATUS_COLORS[status] ?? "bg-muted-foreground/40";
}

/**
 * Role-based colors for the cryod Node timeline: separate hues for the
 * source (checkpoint) and restore (from checkpoint) phases so a cross-node
 * test's two bars are distinguishable at a glance. Same-node tests use the
 * familiar pass-green.
 *
 * Fail/skip override the role hue since a failed run's status signal must
 * dominate — you want to see the red first, then drill into which phase.
 */
export const NODE_TIMELINE_COLORS = {
  source: "bg-sky-500",
  restore: "bg-violet-500",
  single: "bg-emerald-500",
  fail: "bg-rose-500",
  skip: "bg-muted-foreground/40",
} as const;

export type NodeTimelineLaneRole = "source" | "restore" | "single";

export function nodeTimelineBarColor(laneRole: NodeTimelineLaneRole, status: string): string {
  if (status === "skip") return NODE_TIMELINE_COLORS.skip;
  if (status === "fail" || status === "error") return NODE_TIMELINE_COLORS.fail;
  return NODE_TIMELINE_COLORS[laneRole];
}

/**
 * Format a duration in milliseconds as "{m}m {s}s" or "{s}s".
 * Example: 65_000 → "1m 5s", 5_300 → "5s".
 */
export function formatDurationMs(milliseconds: number): string {
  if (milliseconds < 60_000) {
    return `${Math.round(milliseconds / 1000)}s`;
  }
  const minutes = Math.floor(milliseconds / 60_000);
  const seconds = Math.round((milliseconds % 60_000) / 1000);
  if (seconds === 0) return `${minutes}m`;
  return `${minutes}m ${seconds}s`;
}
